import { FC, useState, useCallback } from "react";
import {
  isSelectableDay,
  handleDateClick,
  handleRangeDateClick,
  getSelectionStatus,
  getButtonClass,
} from "@utils/sync/CalendarUtils";
import { Button } from "@components/Helpers/UIComponents";
import Image from "next/image";
import checkIcon from "@public/icons/check.png";
import { CalendarDaysProps } from "@interfaces/uiElementTypes";

export const CalendarDays: FC<CalendarDaysProps> = ({
  calendarDays,
  selectedDate,
  setSelectedDate,
  currentMonth,
  allDaysSelectable = false,
  workingDays = {},
  dateRange = false,
  selectedDateRange,
  setSelectedDateRange,
}) => {
  const [rangeStartSelected, setRangeStartSelected] = useState(false);

  // Memoize handlers to avoid unnecessary re-renders
  const handleDateClickMemo = useCallback(
    (day: Date) => {
      if (dateRange) {
        handleRangeDateClick(
          day,
          selectedDateRange,
          setSelectedDateRange,
          rangeStartSelected,
          setRangeStartSelected,
        );
      } else {
        handleDateClick(
          day,
          selectedDate!,
          setSelectedDate!,
          currentMonth,
          allDaysSelectable,
          workingDays,
        );
      }
    },
    [
      dateRange,
      selectedDate,
      setSelectedDate,
      selectedDateRange,
      setSelectedDateRange,
      rangeStartSelected,
      currentMonth,
      allDaysSelectable,
      workingDays,
    ],
  );

  return (
    <div className="mb-0 grid grid-cols-7 items-center gap-2 text-center text-black md:mb-4">
      {calendarDays.map((day, index) => {
        // Determine if the day is selectable
        const selectable =
          isSelectableDay(day, currentMonth, workingDays) || allDaysSelectable;

        const {
          isSelectedStart,
          isSelectedEnd,
          isInSelectedRange,
          isSelectedSingle,
        } = getSelectionStatus(day, selectedDate, selectedDateRange);

        const buttonClass = getButtonClass(
          day,
          currentMonth,
          selectable,
          isSelectedSingle!,
          isSelectedStart!,
          isSelectedEnd!,
          isInSelectedRange!,
        );

        return (
          <Button
            key={index}
            type="button"
            onClick={() => selectable && handleDateClickMemo(day)}
            className={`nav_button relative mb-2 h-[40px] w-[40px] cursor-pointer ${buttonClass}`}
            disabled={!selectable}
          >
            {day.getDate()}
            {isSelectedSingle && (
              <Image
                src={checkIcon}
                alt="Check Icon"
                width={20}
                height={20}
                className="absolute right-[-7%] top-[-5%]"
              />
            )}
          </Button>
        );
      })}
    </div>
  );
};
